import React from 'react';
import {
  Box,
  BoxProps,
  ButtonProps,
  Divider,
  withStyles,
} from '@material-ui/core';
import FsHeaderSmall from 'ui-elements/typography/FsHeaderSmall';
import FsBody from 'ui-elements/typography/FsBody';
import FsBodySmall from 'ui-elements/typography/FsBodySmall';
import FsButton from 'ui-elements/buttons/FsButton';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import { TypographyAllProps } from 'globals/types';
import { grey } from 'globals/colors';
import cn from 'classnames';

export const Title = withStyles(() => ({
  root: {
    textAlign: 'center',
  },
}))(FsHeaderSmall);

export const SwitchWrapper = withStyles(({ spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    margin: spacing(2, 0),
  },
}))(Box);

export const OptionLabel = withStyles(({ palette, breakpoints }) => ({
  root: {
    fontWeight: 600,
    fontSize: 15,
    color: palette.grey[400],
    width: '100%',
    textAlign: 'center',
    [breakpoints.up('sm')]: {
      width: 'auto',
    },
  },
}))(FsBody);

export const Strong = withStyles(({ palette }) => ({
  root: {
    fontWeight: 600,
    fontSize: 15,
    color: palette.grey[400],
  },
}))((props: TypographyAllProps) => <FsBody {...props} component="span" />);

export const OptionsWrapperBox = withStyles(({ spacing }) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    paddingBottom: spacing(3),
  },
}))(Box);

interface SingleOptionBoxProps {
  isFirst?: boolean;
}

export const SingleOptionBox = withStyles(({ spacing, breakpoints }) => ({
  root: {
    width: '100%',
    textAlign: 'center',
    padding: spacing(1, 4, 0),
    [breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '50%',
    },
    '&.isFirst': {
      borderBottom: `1px solid ${grey.V4}`,
      marginTop: spacing(3),
      marginBottom: spacing(3),
      paddingBottom: spacing(4),
      [breakpoints.up('sm')]: {
        borderBottom: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingBottom: 0,
        borderRight: `1px solid ${grey.V4}`,
      },
    },
  },
}))(({ isFirst, ...otherProps }: BoxProps & SingleOptionBoxProps) => (
  <Box {...otherProps} className={cn({ isFirst })} />
));

export const OptionTitle = withStyles(() => ({
  root: {
    fontWeight: 600,
    fontSize: 18,
  },
}))(FsBody);

export const OptionSubTitle = withStyles(({ palette }) => ({
  root: {
    fontWeight: 300,
    fontSize: 15,
    color: palette.grey[400],
  },
}))(FsBody);

export const Line = withStyles(({ palette, spacing }) => ({
  root: {
    margin: `${spacing(2)}px auto`,
    backgroundColor: palette.primary.main,
    width: 42,
    height: 3,
    borderRadius: 1.5,
  },
}))(Divider);

export const OptionDescription = withStyles(() => ({
  root: {
    fontWeight: 300,
  },
}))(FsBodySmall);

export const OptionMainElementWrapper = withStyles(({ spacing }) => ({
  root: {
    margin: spacing(2, 0),
  },
}))(Box);

export const OptionPriceWrapper = withStyles(({ spacing, palette }) => ({
  root: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
}))(Box);

export const DashedLine = withStyles(({ palette }) => ({
  root: {
    width: '100%',
    height: 1,
    backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23${palette.primary.main.replace(
      '#',
      ''
    )}FF' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
  },
}))(Box);

export const OptionCaption = withStyles(({ palette, spacing }) => ({
  root: {
    color: palette.common.black,
    marginBottom: spacing(0.5),
    textTransform: 'capitalize',
    '& strong': {
      color: palette.primary.main,
      fontWeight: 700,
    },
  },
}))(FsBody);

export const OptionPrice = withStyles(({ palette }) => ({
  root: {
    fontSize: 48,
    fontWeight: 600,
    letterSpacing: -0.24,
    lineHeight: 1,
    color: palette.primary.main,
  },
}))(FsBody);

export const OptionPriceLabel = withStyles(({ palette }) => ({
  root: {
    fontWeight: 700,
    color: palette.primary.main,
  },
}))(FsBodyBold);

export const OptionPriceCurrency = withStyles(({ spacing, palette }) => ({
  root: {
    fontSize: 24,
    fontWeight: 700,
    color: palette.primary.main,
    verticalAlign: 'super',
    marginRight: spacing(0.5),
  },
}))((props: TypographyAllProps) => <FsBodyBold {...props} component="sup" />);

export const Special = withStyles(() => ({
  root: {
    fontWeight: 700,
  },
}))((props: TypographyAllProps) => <FsBodySmall {...props} component="span" />);

export const TriggerButton = withStyles(({ spacing }) => ({
  root: {
    marginTop: spacing(3),
  },
}))((props: ButtonProps) => (
  <FsButton {...props} variant="contained" color="primary" />
));
